import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertScrewBody, Screw, SearchScrewsResponse, UpdateScrewBody } from "./types/screw";

const brScrewsUrl = "/admin/modules/beam_reinforcement/screws";

export const useBRScrewsApi = () => {
  const api = useApi();

  const useBRScrews = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(brScrewsUrl, query);
      return useSWR<SearchScrewsResponse, CustomError, string>(url, (requestUrl) =>
        api.get<SearchScrewsResponse>({ url: requestUrl, signal }),
      );
    },
    [api],
  );

  const getBRScrew = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<Screw, CustomError, string>(`${brScrewsUrl}/${id}`, (url) =>
        api.get<Screw>({ url, signal }),
      );
    },
    [api],
  );

  const insertBRScrew = useCallback(
    async (body: InsertScrewBody): Promise<{ id: string }> => {
      const result = await api.post<{ id: string }, InsertScrewBody>(brScrewsUrl, body);
      mutateCache([brScrewsUrl]);
      return result;
    },
    [api],
  );

  const updateBRScrew = useCallback(
    async (id: string, body: UpdateScrewBody): Promise<void> => {
      const url = `${brScrewsUrl}/${id}`;
      await api.put<UpdateScrewBody>(url, body);
      mutateCache([brScrewsUrl], url);
    },
    [api],
  );

  const deleteBRScrew = useCallback(
    async (id: string): Promise<void> => {
      const url = `${brScrewsUrl}/${id}`;
      await api.del(url);
      mutateCache([brScrewsUrl], url);
    },
    [api],
  );

  const updateBRScrewVisibility = useCallback(
    async (id: string, body: { visibility: string }): Promise<void> => {
      const url = `${brScrewsUrl}/${id}/visibility`;
      await api.put<{ visibility: string }>(url, body);
      mutateCache([brScrewsUrl], url);
    },
    [api],
  );

  const updateBRScrewOrder = useCallback(
    async (id: string, body: { order: number }): Promise<void> => {
      const url = `${brScrewsUrl}/${id}/order`;
      await api.put<{ order: number }>(url, body);
      mutateCache([brScrewsUrl], url);
    },
    [api],
  );

  return {
    useBRScrews,
    getBRScrew,
    insertBRScrew,
    updateBRScrew,
    deleteBRScrew,
    updateBRScrewVisibility,
    updateBRScrewOrder,
  };
};

export default useBRScrewsApi;
